import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Add as AddIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import CreateTicketDialog from 'components/common/CreateTicketDialog';
import type { RootState } from 'redux/store';
import { useGetTicketsQuery } from 'services/api/tickets.api';

import TicketStatusChip from '../Ticket/components/TicketStatusChip';

const Tickets: React.FC = () => {
  const navigate = useNavigate();
  const [includeAll, setIncludeAll] = useState(false);
  const [excludeClosed, setExcludeClosed] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 8;
  const { data, isLoading, refetch } = useGetTicketsQuery({ includeAll, page, pageSize, excludeClosed });

  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const user = useSelector((state: RootState) => state.user);
  const isAdmin = user?.isAdmin;

  const handleTicketCreated = () => {
    refetch().catch((error) => {
      // eslint-disable-next-line no-console
      console.error('Error refetching tickets:', error);
    });
  };

  const handleIncludeAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncludeAll(event.target.checked);
    setPage(1);
  };

  const handleExcludeClosedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExcludeClosed(event.target.checked);
    setPage(1);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleViewTicket = (ticketId: number) => {
    navigate(`/tickets/${ticketId}`);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography>Cargando tickets...</Typography>
      </Box>
    );
  }

  const total = data?.total || 0;
  const tickets = data?.tickets || [];

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Tickets de soporte
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenCreateDialog(true)}>
          Nuevo Ticket
        </Button>
      </Box>

      {/* Filtro de tickets */}
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            variant="body2"
            color={includeAll ? 'text.secondary' : 'primary'}
            fontWeight={includeAll ? 'normal' : 'bold'}
          >
            Solo mis tickets
          </Typography>
          <Switch
            checked={includeAll}
            onChange={handleIncludeAllChange}
            color="primary"
            inputProps={{ 'aria-label': 'Mostrar todos los tickets de mis unidades de negocio' }}
          />
          <Typography
            variant="body2"
            color={includeAll ? 'primary' : 'text.secondary'}
            fontWeight={includeAll ? 'bold' : 'normal'}
          >
            Todos los tickets de mis unidades de negocio
          </Typography>
        </Box>
        <FormControlLabel
          control={<Checkbox checked={excludeClosed} onChange={handleExcludeClosedChange} color="primary" />}
          label="Excluir tickets cerrados"
        />
      </Box>

      {tickets && tickets.length > 0 ? (
        <>
          <Box display="flex" flexDirection="column" gap={1}>
            {tickets.map((ticket) => (
              <Paper
                key={ticket.id}
                elevation={1}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'stretch',
                  justifyContent: 'space-between',
                  flexWrap: 'nowrap',
                  minHeight: 64,
                  gap: 2,
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  '&:hover': {
                    background: 'rgba(25, 118, 210, 0.06)',
                  },
                }}
                onClick={() => handleViewTicket(ticket.id)}
              >
                <Box flex={1} minWidth={60} display="flex" alignItems="center" justifyContent="center">
                  <Typography variant="caption" color="text.secondary" align="center">
                    #{ticket.id}
                  </Typography>
                </Box>
                <Box flex={2} display="flex" flexDirection="column" alignItems="flex-start" justifyContent="center">
                  <Typography variant="caption" color="text.secondary">
                    Por: {ticket.user?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {ticket.businessUnit?.name || 'General'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(ticket.createdAt).toLocaleString('es-CL')}
                  </Typography>
                </Box>
                <Box
                  flex={7}
                  minWidth={0}
                  sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 400,
                    }}
                  >
                    {ticket.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 500,
                    }}
                  >
                    {ticket.description}
                  </Typography>
                </Box>
                {/* Columna: Último mensaje */}
                <Box
                  flex={5}
                  minWidth={0}
                  px={1}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: 500,
                    }}
                  >
                    {ticket.lastMessage}
                  </Typography>
                </Box>
                <Box flex={3} minWidth={0} display="flex" alignItems="center" justifyContent="flex-end" ml={2}>
                  <TicketStatusChip status={ticket.status} size="small" />
                </Box>
              </Paper>
            ))}
          </Box>
          <Box display="flex" justifyContent="flex-end" alignItems="center" mt={2}>
            <Typography variant="body2" color="text.secondary" mr={2}>
              {`${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} de ${total}`}
            </Typography>
            <Pagination
              count={Math.ceil(total / pageSize)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Box>
        </>
      ) : (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="300px">
          <Typography variant="h6" color="text.secondary" mb={2}>
            No hay tickets disponibles
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenCreateDialog(true)}>
            Crear primer ticket
          </Button>
        </Box>
      )}

      <CreateTicketDialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        isAdmin={isAdmin}
        onSuccess={handleTicketCreated}
      />
    </Box>
  );
};

export default Tickets;
